// 동굴 자료 — 동굴마다 부피 선분·빛 웅덩이 자리를 읽어, 카메라가 들어간 동굴 하나를 셰이더 유니폼과 BuildLights 에 싣는다.
// 정본은 시공 스크립트가 쓰는 /data/*.json 이다(CAVE_SOURCES). 좌표를 여기 적지 마라.
// 누가 부르나: CaveCrystalFx(매 프레임 selectCaveAt → 게이트·안개·먼지) · BuildLights(0.6초 재정렬의 빛 후보).
import { setCaveCrackZone } from './biome/biomeCrack';

export const CAVE_CRYSTAL_URL = '/data/cave_crystal_cave.json';
/** 동굴 목록 — url · 테마(biomeCrack.CAVE_THEMES 키). 부피 게이트 유니폼은 한 벌이라 한 번에 한 동굴만 켜진다. */
export const CAVE_SOURCES = [
  { url: CAVE_CRYSTAL_URL, theme: 'geode' },          // 후암 수정 동굴 — place_cave_crystals.py
  { url: '/data/sillim_abyss_cave.json', theme: 'magma' },   // 신림 심연 던전 — carve_sillim_abyss.py
  { url: '/data/umyeon_realm_cave.json', theme: 'starlight' },   // 우면산 지저 왕국 — carve_umyeon_realm.py
];
/** 빛 색(선형에 가까운 0~1) — 0 자수정 · 1 청록 · 2 라벤더 · 3 분홍 · 4 호박 · 5 마그마 주황 · 6 심연 청람. */
export const CRYSTAL_LIGHT_RGB = [[0.62, 0.36, 1.0], [0.2, 0.95, 0.82], [0.72, 0.6, 1.0], [0.95, 0.42, 0.9], [1.0, 0.58, 0.2],
  [1.0, 0.36, 0.08], [0.25, 0.48, 1.0]];
const CRYSTAL_LIGHT_GAIN = 0.9;
/** 빛 자리마다 고정 난수(0~1) — 좌표 해시라 새로고침해도 같은 빛이 같은 세기다. */
const lightRnd = (x, y, z) => { const s = Math.sin(x * 12.9898 + y * 78.233 + z * 37.719) * 43758.5453; return s - Math.floor(s); };

/** 지금 켜진 동굴 — 소비자는 이 객체만 본다. rev 는 동굴이 바뀔 때마다 오른다. */
export const caveCrystal = { ready: false, key: null, theme: 'geode', rev: 0, box: null, mouths: [], fade: 0, lights: [], shafts: [], glows: [], falls: [], lake: null };
const caves = [];
let _loading = null;

function parse(doc, src) {
  if (!doc?.zone?.segs?.length) return null;
  return {
    key: src.url, theme: doc.theme || src.theme, zone: doc.zone, box: doc.zone.box, mouths: doc.zone.mouths || [], fade: doc.zone.fade || 0,
    // 세기 0.35~1배(어두운 쪽으로 치우침) · 반경 0.7~1배를 빛마다 다르게 — 고른 세기면 굴 전체가 한 톤으로 번쩍인다.
    lights: (doc.lights || []).map(([x, y, z, r, c, g]) => {
      const h = lightRnd(x, y, z);
      const h2 = lightRnd(z, x, y);
      return { x, y, z, r: r * (0.7 + 0.3 * h2), g: (g ?? 1) * (0.35 + 0.65 * h * h), rgb: CRYSTAL_LIGHT_RGB[c] || CRYSTAL_LIGHT_RGB[0] };
    }),
    shafts: doc.shafts || [], glows: doc.glows || [], falls: doc.falls || [], lake: doc.lake || null,
  };
}

function activate(cv) {
  if (!cv || caveCrystal.key === cv.key) return;
  setCaveCrackZone(cv.zone, cv.theme);
  Object.assign(caveCrystal, { key: cv.key, theme: cv.theme, box: cv.box, mouths: cv.mouths, fade: cv.fade, lights: cv.lights, shafts: cv.shafts,
    glows: cv.glows, falls: cv.falls, lake: cv.lake, ready: true });
  caveCrystal.rev += 1;
}

/** 자료를 한 번 읽는다. 실패한 동굴은 빠지고, 하나도 없으면 부피가 비어 셰이더 분기가 영영 안 켜진다. */
export function loadCaveCrystal() {
  if (_loading) return _loading;
  _loading = Promise.all(CAVE_SOURCES.map((src) => fetch(src.url)
    .then((r) => (r.ok ? r.json() : null))
    .then((doc) => parse(doc, src))
    .catch(() => null)))
    .then((list) => {
      for (const cv of list) if (cv) caves.push(cv);
      activate(caves[0]);
      return caveCrystal;
    });
  return _loading;
}

const inBox = (b, x, z, pad) => !!b && x > b[0] - pad && z > b[1] - pad && x < b[2] + pad && z < b[3] + pad;

/** (x,z) 를 덮는 동굴로 바꾼다. 어느 동굴 상자에도 없으면 지금 것을 그대로 둔다(밖에서는 어차피 꺼진다). */
export function selectCaveAt(x, z, pad = 40) {
  if (caves.length < 2 || inBox(caveCrystal.box, x, z, 0)) return;
  for (const cv of caves) if (inBox(cv.box, x, z, pad)) { activate(cv); return; }
}

/** 모든 동굴의 빛 자리 — 먼 불빛처럼 동굴 하나에 묶이지 않는 소비자용. loadCaveCrystal 이 끝난 뒤에 부른다. */
export const allCaveLights = () => caves.flatMap((cv) => cv.lights);

/** 지금 켜진 동굴의 갱구 r m 안인가(xz) — 밖에서 갱구를 볼 때도 페이드된 테마를 켠다. 페이드가 없는 동굴은 거짓. */
export function nearCaveMouth(x, z, r) {
  if (!caveCrystal.fade) return false;
  return caveCrystal.mouths.some(([mx, mz]) => (mx - x) ** 2 + (mz - z) ** 2 < r * r);
}

/** 점이 지금 켜진 동굴 상자 안인가(xz). 자료가 없으면 거짓. */
export function inCaveCrystalBox(x, z, pad = 0) {
  return inBox(caveCrystal.box, x, z, pad);
}

/** BuildLights 후보 — 반경 r 안의 결정 빛 자리. 반환 항목은 caveLight.setCaveLights 형식(+ d2·d3·up·key). */
export function caveCrystalLightCandidates(p, r) {
  if (!caveCrystal.ready || !inCaveCrystalBox(p.x, p.z, r)) return [];
  const out = [];
  const r2 = r * r;
  const L = caveCrystal.lights;
  for (let i = 0; i < L.length; i += 1) {
    const l = L[i];
    const dx = l.x - p.x, dz = l.z - p.z, dy = l.y - p.y;
    const d2 = dx * dx + dz * dz;
    if (d2 > r2) continue;
    out.push({ key: `cc${caveCrystal.rev}_${i}`, x: l.x, y: l.y, z: l.z, r: l.r, rgb: l.rgb, gain: CRYSTAL_LIGHT_GAIN * l.g, d2, d3: d2 + dy * dy, up: dy });
  }
  return out;
}

// 계측용 — 페이지 안 import() 는 다른 사본이 답할 수 있어 앱이 쓰는 홀더를 창에 건다.
if (typeof window !== 'undefined') window.__caveCrystal = caveCrystal;
